import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import axios from "axios"

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [account, setAccount] = useState(null)
  const [token, setToken] = useState(
    localStorage.getItem("odontoflow_token") || ""
  )
  const [loading, setLoading] = useState(true)

  const clearSessionStorage = useCallback(() => {
    localStorage.removeItem("odontoflow_token")
    localStorage.removeItem("odontoflow_clinic_id")
    localStorage.removeItem("odontoflow_user")
  }, [])

  const applySession = useCallback((data, sessionToken) => {
    if (sessionToken) {
      localStorage.setItem("odontoflow_token", sessionToken)
      setToken(sessionToken)
    }

    setUser(data?.user || null)
    setAccount(data?.account || null)

    if (data?.user) {
      localStorage.setItem("odontoflow_user", JSON.stringify(data.user))
    }

    if (data?.user?.clinicId) {
      localStorage.setItem("odontoflow_clinic_id", data.user.clinicId)
    }
  }, [])

  const logout = useCallback(() => {
    clearSessionStorage()
    delete axios.defaults.headers.common.Authorization

    setUser(null)
    setAccount(null)
    setToken("")
    setLoading(false)
  }, [clearSessionStorage])

  const login = useCallback(
    async (email, password) => {
      const { data } = await axios.post("/api/auth/login", {
        email,
        password,
      })

      const sessionToken = data?.token

      if (!sessionToken) {
        throw new Error("No se recibió token del backend")
      }

      applySession(data, sessionToken)
      setLoading(false)

      return data
    },
    [applySession]
  )

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common.Authorization
    }
  }, [token])

  useEffect(() => {
    let cancelled = false

    async function loadMe() {
      if (!token) {
        setUser(null)
        setAccount(null)
        setLoading(false)
        return
      }

      setLoading(true)

      try {
        const { data } = await axios.get("/api/auth/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (cancelled) return

        setUser(data?.user || null)
        setAccount(data?.account || null)

        if (data?.user) {
          localStorage.setItem("odontoflow_user", JSON.stringify(data.user))
        }

        if (data?.user?.clinicId) {
          localStorage.setItem("odontoflow_clinic_id", data.user.clinicId)
        }
      } catch (err) {
        if (cancelled) return
        console.warn("Token inválido o expirado")
        logout()
        return
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadMe()

    return () => {
      cancelled = true
    }
  }, [token, logout])

  const value = useMemo(
    () => ({
      user,
      account,
      token,
      loading,
      isAuthenticated: !!token,
      login,
      logout,
    }),
    [user, account, token, loading, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }

  return context
}